import { forwardRef, useRef, type HTMLAttributes, type ReactNode, type MouseEvent } from "react";
import { motion, useMotionValue, useSpring, useTransform, useReducedMotion } from "framer-motion";
import { Card } from "@/components/ui/card";
import { cn } from "@/lib/utils";

interface GlossyCardProps extends Omit<HTMLAttributes<HTMLDivElement>, "onAnimationStart" | "onDrag" | "onDragEnd" | "onDragStart"> {
  /** Optional numbered badge shown in the top-left of the card. */
  step?: number;
  /** Tone influences the gradient hue. */
  tone?: "primary" | "accent" | "neutral";
  /** Disable the continuous shimmer overlay (keeps only sheen + lift). */
  shimmer?: boolean;
  /** Disable the 3D tilt-on-hover effect. */
  tilt?: boolean;
  children: ReactNode;
}

/**
 * Glossy card wrapper used across TryOn, Style, Aura and Profile.
 *
 * Layers (z-order, bottom-up):
 *   1. Card surface (from shadcn)
 *   2. Gradient backdrop (subtle primary↔accent wash)
 *   3. Continuous shimmer (slow diagonal sweep)
 *   4. Hover sheen (sweeps across once on hover)
 *   5. Cursor-tracking glow (3D depth cue)
 *   6. Content (relative z-10)
 *
 * The wrapper adds:
 *   - 3D tilt that follows the cursor (disabled if user prefers reduced motion)
 *   - Hover lift + elevated shadow
 */
export const GlossyCard = forwardRef<HTMLDivElement, GlossyCardProps>(
  ({ step, tone = "primary", shimmer = true, tilt = true, className, children, ...props }, ref) => {
    const gradient =
      tone === "accent"
        ? "from-accent/10 via-transparent to-primary/5"
        : tone === "neutral"
          ? "from-foreground/[0.03] via-transparent to-foreground/[0.02]"
          : "from-primary/10 via-transparent to-accent/10";

    const prefersReducedMotion = useReducedMotion();
    const enableTilt = tilt && !prefersReducedMotion;

    const innerRef = useRef<HTMLDivElement | null>(null);

    // Normalised cursor position (-0.5 → 0.5)
    const x = useMotionValue(0);
    const y = useMotionValue(0);

    // Springy rotation values (max ~6deg)
    const rotateX = useSpring(useTransform(y, [-0.5, 0.5], [6, -6]), { stiffness: 200, damping: 18 });
    const rotateY = useSpring(useTransform(x, [-0.5, 0.5], [-6, 6]), { stiffness: 200, damping: 18 });

    // Glow position (% across the card)
    const glowX = useTransform(x, [-0.5, 0.5], ["0%", "100%"]);
    const glowY = useTransform(y, [-0.5, 0.5], ["0%", "100%"]);

    const handleMouseMove = (e: MouseEvent<HTMLDivElement>) => {
      if (!enableTilt || !innerRef.current) return;
      const rect = innerRef.current.getBoundingClientRect();
      x.set((e.clientX - rect.left) / rect.width - 0.5);
      y.set((e.clientY - rect.top) / rect.height - 0.5);
    };

    const handleMouseLeave = () => {
      x.set(0);
      y.set(0);
    };

    const setRefs = (node: HTMLDivElement | null) => {
      innerRef.current = node;
      if (typeof ref === "function") ref(node);
      else if (ref) (ref as React.MutableRefObject<HTMLDivElement | null>).current = node;
    };

    return (
      <motion.div
        style={
          enableTilt
            ? { rotateX, rotateY, transformStyle: "preserve-3d", perspective: 1000 }
            : undefined
        }
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
        className="will-change-transform"
      >
        <Card
          ref={setRefs}
          className={cn(
            "group/glossy relative overflow-hidden p-4 shadow-soft transition-all duration-300",
            "hover:-translate-y-0.5 hover:shadow-elevated",
            className,
          )}
          {...props}
        >
          {/* 2. Gradient backdrop */}
          <div
            aria-hidden
            className={cn("pointer-events-none absolute inset-0 bg-gradient-to-br", gradient)}
          />

          {/* 3. Continuous shimmer */}
          {shimmer && (
            <div
              aria-hidden
              className="pointer-events-none absolute inset-0 bg-[linear-gradient(110deg,transparent_30%,hsl(var(--primary)/0.07)_50%,transparent_70%)] bg-[length:200%_100%] animate-shimmer"
            />
          )}

          {/* 4. Hover sheen sweep */}
          <div
            aria-hidden
            className="pointer-events-none absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-primary-foreground/10 to-transparent transition-transform duration-1000 ease-out group-hover/glossy:translate-x-full"
          />

          {/* 5. Cursor-tracking glow (only when tilt enabled) */}
          {enableTilt && (
            <motion.div
              aria-hidden
              style={{
                background: useTransform(
                  [glowX, glowY],
                  ([gx, gy]: string[]) =>
                    `radial-gradient(circle at ${gx} ${gy}, hsl(var(--primary) / 0.18), transparent 55%)`,
                ),
              }}
              className="pointer-events-none absolute inset-0 opacity-0 transition-opacity duration-300 group-hover/glossy:opacity-100"
            />
          )}

          {/* Optional numbered badge */}
          {step !== undefined && (
            <span
              aria-hidden
              className="absolute -left-px -top-px z-10 flex h-7 min-w-7 items-center justify-center rounded-br-2xl rounded-tl-[inherit] bg-gradient-to-br from-primary to-primary/80 px-2 text-[11px] font-bold text-primary-foreground shadow-[0_2px_12px_-2px_hsl(var(--primary))]"
              style={enableTilt ? { transform: "translateZ(20px)" } : undefined}
            >
              {step}
            </span>
          )}

          {/* 6. Content */}
          <div
            className={cn("relative", step !== undefined && "pt-1")}
            style={enableTilt ? { transform: "translateZ(15px)" } : undefined}
          >
            {children}
          </div>
        </Card>
      </motion.div>
    );
  },
);

GlossyCard.displayName = "GlossyCard";

/**
 * Small button-style helper that gets the same hover lift + sheen treatment.
 * Use as a className addition on existing Buttons (`className={glossyButtonClass}`).
 */
export const glossyButtonClass =
  "transition-all duration-200 hover:-translate-y-0.5 hover:shadow-[0_8px_24px_-12px_hsl(var(--primary)/0.5)]";
